// Questionnaire de positionnement (onboarding). Obligatoire avant l'accès à la
// saisie : score ≥ 80 % = accès direct ; sinon proposition de formation AERH
// (25 000 F TTC) ou poursuite avec décharge de responsabilité.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GraduationCap, Loader2, CheckCircle2, AlertTriangle, ShieldAlert } from "lucide-react";

const fmt = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} F`;

export default function Quiz() {
  const navigate = useNavigate();
  const { company, setCompany } = useAuth();
  const [data, setData] = useState(null);
  const [reponses, setReponses] = useState({});
  const [resultat, setResultat] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [envoi, setEnvoi] = useState(false);
  const [dechargeCochee, setDechargeCochee] = useState(false);

  useEffect(() => {
    api.get("/onboarding/quiz").then(({ data }) => {
      setData(data);
      if (data.acces_saisie) navigate("/tableau-de-bord", { replace: true });
    }).catch(() => toast.error("Impossible de charger le questionnaire.")).finally(() => setChargement(false));
  }, [navigate]);

  const soumettre = async () => {
    if (Object.keys(reponses).length < (data?.questions.length || 0)) {
      return toast.error("Merci de répondre à toutes les questions.");
    }
    setEnvoi(true);
    try {
      const { data: r } = await api.post("/onboarding/quiz", { reponses });
      setResultat(r);
      setCompany(r.entreprise);
      if (r.reussi) {
        toast.success(`Bravo ! Score ${r.score} % — accès débloqué.`);
        setTimeout(() => navigate("/tableau-de-bord", { replace: true }), 1500);
      }
    } catch {
      toast.error("L'envoi a échoué. Veuillez réessayer.");
    } finally {
      setEnvoi(false);
    }
  };

  const accepterDecharge = async () => {
    if (!dechargeCochee) {
      toast.error("Veuillez cocher la case d'acceptation de la décharge.");
      return;
    }
    setEnvoi(true);
    try {
      const { data: r } = await api.post("/onboarding/decharge", { accepte: true });
      setCompany(r.entreprise);
      toast.success("Décharge acceptée — accès débloqué. Une copie a été archivée par AERH.");
      navigate("/tableau-de-bord", { replace: true });
    } catch {
      toast.error("Action impossible. Veuillez réessayer.");
    } finally {
      setEnvoi(false);
    }
  };

  const refaire = () => { setResultat(null); setReponses({}); setDechargeCochee(false); };

  if (chargement) {
    return <div className="min-h-screen flex items-center justify-center bg-[#E8F4F4]"><Loader2 className="h-8 w-8 animate-spin text-[#1D3D3D]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#E8F4F4] py-10 px-4" data-testid="page-quiz">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-[#1D3D3D] flex items-center justify-center">
            <GraduationCap className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-[#1D3D3D]">Questionnaire de positionnement</h1>
            <p className="text-sm text-[#1D3D3D]/70">{company?.raison_sociale} · Un score d'au moins {data?.seuil} % donne accès à la saisie.</p>
          </div>
        </div>

        {/* Résultat échec : proposition formation + décharge */}
        {resultat && !resultat.reussi ? (
          <div data-testid="resultat-echec" className="bg-white border border-[#4A8A8A]/20 rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-3 text-[#8A4A4A]">
              <AlertTriangle className="h-7 w-7" />
              <div>
                <p className="font-bold text-lg" data-testid="quiz-score">Score : {resultat.score} % ({resultat.bonnes_reponses}/{resultat.total})</p>
                <p className="text-sm text-[#1D3D3D]/70">En dessous de {data.seuil} %, une formation est recommandée avant de saisir votre comptabilité.</p>
              </div>
            </div>
            <div className="rounded-lg bg-[#E8F4F4] border border-[#4A8A8A]/20 p-4">
              <p className="font-bold text-[#1D3D3D]">Formation AERH — Prise en main comptable</p>
              <p className="text-2xl font-black text-[#1D3D3D] mt-1" data-testid="montant-formation">{fmt(resultat.montant_formation)} TTC</p>
              {(resultat.duree_formation || data.duree_formation) && (
                <p className="text-sm font-semibold text-[#2D5A5A]" data-testid="duree-formation">Durée : {resultat.duree_formation || data.duree_formation}</p>
              )}
              <p className="text-xs text-[#1D3D3D]/70 mt-1">Contactez-nous : {data.contact} · {data.telephone}</p>
            </div>
            <div className="rounded-lg border border-[#8A4A4A]/30 bg-[#8A4A4A]/5 p-4 space-y-3">
              <p className="text-xs text-[#1D3D3D]/80 leading-relaxed">
                En continuant sans formation malgré un score insuffisant, je reconnais utiliser
                l'application <strong>sous ma seule et entière responsabilité</strong>. Je décharge
                expressément le <strong>Groupe AERH Polynésie</strong> de toute responsabilité quant
                aux erreurs de saisie, omissions ou conséquences comptables, fiscales ou légales
                résultant de mon utilisation de l'application.
              </p>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="case-decharge"
                  checked={dechargeCochee}
                  onChange={(e) => setDechargeCochee(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#8A4A4A]/40 text-[#1D3D3D] focus:ring-[#4A8A8A] cursor-pointer"
                />
                <span className="text-sm font-semibold text-[#1D3D3D]">
                  J'ai lu et j'accepte la décharge de responsabilité ci-dessus.
                </span>
              </label>
              <p className="text-[11px] text-[#1D3D3D]/50">
                Votre acceptation est horodatée, associée à votre compte et une copie est archivée par AERH.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button data-testid="bouton-refaire" onClick={refaire} variant="secondary"
                className="bg-[#E8F4F4] text-[#1D3D3D] border border-[#4A8A8A]/30 hover:bg-[#4A8A8A]/20">
                Refaire le questionnaire
              </Button>
              <Button data-testid="bouton-decharge" onClick={accepterDecharge} disabled={envoi || !dechargeCochee}
                className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                <ShieldAlert className="h-4 w-4 mr-1" />
                Continuer quand même
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-[#4A8A8A]/20 rounded-xl p-6 space-y-6">
            {data?.questions.map((q, i) => (
              <div key={q.id} data-testid={`question-${q.id}`} className="space-y-2">
                <p className="font-semibold text-[#1D3D3D] text-sm">{i + 1}. {q.question}</p>
                <div className="grid gap-2">
                  {q.options.map((opt, idx) => {
                    const choisi = reponses[q.id] === idx;
                    return (
                      <button key={idx} type="button" data-testid={`option-${q.id}-${idx}`}
                        onClick={() => setReponses((r) => ({ ...r, [q.id]: idx }))}
                        className={`text-left text-sm rounded-lg border px-3 py-2 transition-colors ${choisi
                          ? "bg-[#1D3D3D] text-white border-[#1D3D3D]" : "bg-white text-[#1D3D3D] border-[#4A8A8A]/30 hover:bg-[#E8F4F4]"}`}>
                        {choisi && <CheckCircle2 className="h-4 w-4 inline mr-1.5 -mt-0.5" />}{opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <Button data-testid="bouton-soumettre-quiz" onClick={soumettre} disabled={envoi}
              className="w-full bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
              {envoi ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Valider mes réponses
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
